import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { chatApi, governanceApi, type AskContextHint, type ConversationSummary } from '../services/api'
import {
  isLiveProposal,
  proposalExpired,
  proposalOutcome,
  withoutToken,
  type ProposalAction,
  type ProposalOutcome,
} from '../components/chat/proposalPolicy'
import { useWorkspaceStore } from '../stores/workspaceStore'
import type { Message } from '../types/chat'
import { MESSAGE_TOO_LONG, tooLongCopy } from './askLimits'

/**
 * The Ask dock's conversation: the thread list, the open transcript, and
 * sending.
 *
 * **Everything here is scoped to the live workspace AT THE READ**, the way
 * `useAskEntityState` scopes the pin — never cleared in an effect. An
 * effect-based reset still renders one frame of workspace A's transcript under
 * workspace B (the old `/ask` screen, `ChatInterface`, did exactly that), and one
 * frame is one too many when the frame is a conversation about another store's
 * customers. The old popup, `AskOverlay`, had no reset at all and would have
 * posted B's `workspaceId` against A's `conversationId`. Both were deleted in
 * MAR-174 PR 2; `/ask` and `/chat` now only redirect into this dock.
 *
 * Loading thread history is **new behaviour**, not a port: the popup never
 * loaded any, and the old `/ask` screen loaded it only while it was open.
 *
 * ⚠️ One store, re-claimed per workspace — deliberately UNLIKE the ask-entity
 * pin, which masks the previous workspace's value and resurfaces it on the way
 * back. A pin is a reference to something on screen and cheap to hold; a
 * transcript is a document the server already keeps. Switching away discards
 * the in-memory copy and the thread stays in "Your threads", one click from
 * being reopened. Holding a live transcript per workspace would be a cache with
 * no eviction and a second workspace boundary to get wrong.
 */

/**
 * The one proposal that can still be acted on (MAR-175), with its one-time token.
 *
 * ⚠️ **The token lives here and nowhere else**: not on a `Message`, not in
 * localStorage or sessionStorage, not in a URL. It sits inside `ThreadStore`
 * so that it inherits the store's scoping — the workspace AND the thread epoch —
 * and it is additionally pinned to the `conversationId` the reply came back
 * on, so the read at the bottom of this file can refuse it for any thread but
 * its own. It is dropped the moment it is spent (any answer but `retry`).
 *
 * One slot, not a map: the newest proposal replaces the last, and an older
 * card in the transcript simply becomes inert.
 */
interface LiveProposal {
  conversationId: string
  receiptId: string
  /** Absent once spent. */
  token?: string
  expiresAt: string
  working: boolean
  outcome?: ProposalOutcome
}

/** Everything the dock holds, stamped with the workspace it belongs to. */
interface ThreadStore {
  /** Whose data this is. The scoping read at the bottom of this file uses it. */
  workspaceId: string | undefined
  threads: ConversationSummary[]
  threadsState: 'idle' | 'loading' | 'ready' | 'error'
  messages: AskMessage[]
  /** Server-minted. The client never generates one — see `send`. */
  conversationId?: string
  /**
   * Which thread is open, as a client-side epoch.
   *
   * ⚠️ NOT `conversationId`: a brand-new thread has no server id until its
   * first reply lands, so the id cannot tell "the thread I started this turn
   * in" from "the thread I started after pressing New thread". Minted by
   * everything that changes which thread is on screen — `claim` when it builds
   * a store, `openThread`, `newThread` — and captured by `send` so a late reply
   * knows whether it is still wanted.
   *
   * ⚠️ **What makes this safe is that EVERY `claim` mints, not that `0` is
   * avoided.** The bug was a rebuilt store inheriting `EMPTY`'s `0` while a
   * first turn had also captured `0`: round-trip the workspace and the two
   * meet. Minting per claim is the guard, and mutating it away kills four
   * tests.
   *
   * `0` is additionally a sentinel meaning "never claimed" — `threadEpoch` is
   * pre-incremented, so the first number handed out is `1`, and any store
   * carrying a `workspaceId` carries `>= 1`. That is worth knowing when reading
   * a store in a debugger, but it is a *property, not a mechanism*: making the
   * counter post-increment (so `0` becomes live) kills no test, because nothing
   * ever compares a live store against `EMPTY`'s value. Do not mistake it for
   * the thing doing the work.
   */
  threadSeq: number
  sending: boolean
  /** A thread being fetched. The transcript is empty until it lands. */
  opening: boolean
  /** That fetch failed. Named rather than leaving the merchant on the old one. */
  openFailed: boolean
  /** See `LiveProposal`. Never read directly — only through the scoped read below. */
  live?: LiveProposal
}

const EMPTY: ThreadStore = {
  workspaceId: undefined,
  threads: [],
  threadsState: 'idle',
  messages: [],
  conversationId: undefined,
  threadSeq: 0,
  sending: false,
  opening: false,
  openFailed: false,
  live: undefined,
}

/**
 * What the dock may know about the live proposal — **never the token**. A
 * component holds a `receiptId` and asks the hook to act on it; the token stays
 * behind this boundary.
 */
export interface LiveProposalView {
  receiptId: string
  working: boolean
  outcome?: ProposalOutcome
  /** Has an unspent token. False once confirmed, cancelled or refused. */
  actionable: boolean
}

export interface AskThread {
  threads: ConversationSummary[]
  threadsState: ThreadStore['threadsState']
  messages: AskMessage[]
  conversationId?: string
  sending: boolean
  opening: boolean
  openFailed: boolean
  /** Load one thread's history into the transcript. */
  openThread: (conversationId: string) => void
  /** Drop the transcript and start again. Deletes nothing server-side. */
  newThread: () => void
  /**
   * Ask. `context` is the caller's hint about what was on screen, built by
   * `askContext.ts` `toContextHint` — **passed per call, never held**. See
   * `send` for why it is an argument rather than a hook parameter.
   */
  send: (text: string, context?: AskContextHint, options?: SendOptions) => Promise<SendOutcome>
  /**
   * The proposal on screen that can still be confirmed, scoped at the read to
   * this workspace AND this thread. `null` for everything else — a reloaded
   * proposal included, which is shown but never confirmable.
   */
  liveProposal: LiveProposalView | null
  /**
   * Confirm or cancel the live proposal named by `receiptId`. The ONLY path to
   * `governanceApi.confirmProposal`: nothing in `send` — and so nothing a
   * merchant types — reaches it. A no-op when `receiptId` is not the live one,
   * when it is spent or expired, or while a press is already in flight.
   */
  actOnProposal: (receiptId: string, action: ProposalAction) => Promise<void>
}

/**
 * What became of a turn, for the composer. Only `too_long` changes what the
 * dock does — it puts the merchant's text back so a refusal cannot lose it.
 * `not_sent` covers every turn that never reached the wire or whose reply was
 * dropped because the merchant moved on.
 */
export type SendOutcome = 'answered' | 'too_long' | 'failed' | 'not_sent'

export const UNREACHABLE = "I couldn't reach the store just now — try again in a moment."

/**
 * Why a turn did not land (D-38). It rides the merchant's OWN message — the
 * turn that was not answered — and is never written as a Marva message.
 *
 * ⚠️ Client-only and in-memory. merchant-chat persists a turn only once it is
 * answered (user + assistant together, `history-store.js` `newTurns`), so a
 * failed turn has no server row and a reloaded thread cannot carry this — nor
 * the phantom "answer" the old code appended in its place.
 *
 * `retryable` is false for `too_long`: resending the same text can never fix a
 * length refusal; trimming can, and the copy says so.
 */
export interface AskFailure {
  kind: 'unreachable' | 'too_long'
  /** The sentence the notice shows. */
  copy: string
  retryable: boolean
}

/** A transcript message as the dock holds it: a user turn may carry its failure. */
export type AskMessage = Message & { failure?: AskFailure }

export interface SendOptions {
  /**
   * Try again (D-38): the id of the failed user message this resends. The
   * failed message is taken out and the text is asked again as a fresh turn, so
   * the notice gives way to the pending dots. Refused unless that message is
   * still in the transcript AND still failed AND retryable — and only once per
   * failed message, however fast the button is pressed.
   */
  retryOf?: string
}

export function useAskThread(
  liveWorkspaceId: string | undefined,
  accountId: string | undefined
): AskThread {
  const [stored, setStored] = useState<ThreadStore>(EMPTY)

  // A committed snapshot for the event handlers to read. Event handlers run
  // after commit, so this is current when `send` reads it — and reading it here
  // rather than closing over `stored` is what stops `send` posting the
  // conversation id of whatever thread was open when it was last rebuilt.
  const storeRef = useRef(stored)
  /**
   * Work that must wait until a patch has COMMITTED — i.e. until `storeRef`
   * shows it. Drained by the same effect, after it refreshes the ref.
   */
  const afterCommit = useRef<Array<() => void>>([])
  useEffect(() => {
    storeRef.current = stored
    const pending = afterCommit.current.splice(0)
    for (const run of pending) run()
  })

  /**
   * Is `asked` still the workspace on screen? Read from the store at CALL time
   * — a value captured in a closure is exactly what goes stale across a switch.
   * The same shape as `AuthorityPanel.tsx:184` and its six sibling sites.
   */
  const stillCurrent = useCallback(
    (asked: string) => useWorkspaceStore.getState().currentWorkspace?.id === asked,
    []
  )

  /**
   * Apply a patch only if the store is still the one the request was made
   * against — **the same workspace AND the same thread**.
   *
   * ⚠️ **Name every dimension.** This guard has now been got wrong three times
   * in this item, each time by naming one dimension and missing another: the
   * pin guarded the record and not the workspace, the stored dock preference
   * carried a band tag written by the wrong band, and this function guarded
   * `workspaceId` alone. With one dimension, asking in thread A and opening
   * thread B before the answer arrived appended A's reply to B and flipped
   * `conversationId` back to A — so the merchant read B and their next message
   * continued A. A fourth dimension will arrive one day; add it **here**, and
   * to `ThreadStore`, so that every awaited write inherits it at once.
   *
   * Deliberately not the same guard as `stillCurrent`: that one stops a late
   * response doing anything at all — including re-fetching the thread list for
   * a workspace nobody is looking at — while this one stops a write landing on
   * a store it does not belong to. They overlap on purpose.
   *
   * ⚠️ We DROP the stale reply rather than disabling the controls while a send
   * is in flight. Locking "New thread" and the history rows for the length of a
   * model call makes the dock feel broken for a second or two, for a race that
   * is rare; and a merchant who has moved on has told us, by moving, which
   * thread they care about. The turn is not lost — the server has it, and the
   * thread carries it when reopened.
   */
  const patchIfCurrent = useCallback(
    (asked: string, seq: number, patch: (s: ThreadStore) => ThreadStore) =>
      setStored((s) => (s.workspaceId === asked && s.threadSeq === seq ? patch(s) : s)),
    []
  )

  /**
   * Request ordering, one counter per kind of read.
   *
   * ⚠️ The thread LIST needs its own, because a list belongs to a workspace and
   * not to a thread, so `threadSeq` cannot order it — and a send refreshes it,
   * which is how two list reads end up in flight at once.
   *
   * ⚠️ `openThread` deliberately has NO second counter. It bumps the thread
   * epoch, and `patchIfCurrent` already refuses anything carrying an older one,
   * so a separate `openSeq` was provably redundant — mutation-testing it
   * changed no behaviour and killed no test. Two counters bumped in lockstep
   * are one counter and one thing to get out of step later. Same shape as
   * `Journal.tsx`'s `storySeq` and `AuthorityPanel.tsx:167`, applied once.
   */
  const listSeq = useRef(0)
  /**
   * The next thread epoch. A monotonic ref, not a value derived inside a state
   * updater: updaters must be pure (React may run one twice), and two clicks in
   * the same tick would otherwise both read the same `threadSeq` and mint the
   * same epoch. The store still CARRIES the epoch — that is what lets
   * `patchIfCurrent` name the dimension — but only this ref hands them out.
   */
  const threadEpoch = useRef(EMPTY.threadSeq)
  /** The next epoch. Pre-incremented, so it never hands out the `0` sentinel. */
  const mintEpoch = useCallback(() => ++threadEpoch.current, [])

  /**
   * Start, or adopt, this workspace's store.
   *
   * ⚠️ **A store it BUILDS gets a freshly minted epoch**, never `EMPTY`'s. A
   * rebuilt store used to inherit `0`, and `0` was also what the first turn in
   * a workspace captured: send in A, switch to B and back — the `loadThreads`
   * effect rebuilds A at `0` — and the reply matched the empty store. The
   * merchant saw an answer with no question above it, in a thread the dock then
   * silently continued. Monotonic was not enough; it has to be unique.
   *
   * The mint happens **before** `setStored`, not inside the updater: updaters
   * must be pure, and React may run one twice under StrictMode, which would
   * burn two epochs and let the two invocations disagree. An epoch minted and
   * then not used (because the store was adopted) is simply skipped — the
   * counter only has to be monotonic, not gapless.
   */
  const claim = useCallback(
    (asked: string, patch: (s: ThreadStore) => ThreadStore) => {
      const fresh = mintEpoch()
      setStored((s) =>
        patch(s.workspaceId === asked ? s : { ...EMPTY, workspaceId: asked, threadSeq: fresh })
      )
    },
    [mintEpoch]
  )

  const loadThreads = useCallback(
    async (asked: string) => {
      const seq = ++listSeq.current
      claim(asked, (s) => ({ ...s, threadsState: 'loading' }))
      let res
      try {
        res = await chatApi.listConversations(asked)
      } catch {
        res = null
      }
      // Nothing is written for a workspace the merchant has left — not even a
      // failure state, which would otherwise surface on the store they moved to.
      if (!stillCurrent(asked)) return
      // An older list must never overwrite a newer one.
      if (seq !== listSeq.current) return
      setStored((s) =>
        s.workspaceId !== asked
          ? s
          : res?.success && res.data
            ? { ...s, threads: res.data.conversations, threadsState: 'ready' }
            : // A list that could not be read is NOT an empty list. §3.4: a
              // failed read must never be presented as "nothing exists".
              { ...s, threadsState: 'error' }
      )
    },
    [claim, stillCurrent]
  )

  // Fetching on a workspace change is an effect; *clearing* is not. What the
  // consumer reads is already scoped below, so this only decides when to ask
  // the server — it is never what makes the previous workspace's data go away.
  useEffect(() => {
    if (!liveWorkspaceId) return
    loadThreads(liveWorkspaceId)
  }, [liveWorkspaceId, loadThreads])

  const openThread = useCallback(
    async (conversationId: string) => {
      const asked = liveWorkspaceId
      if (!asked) return
      // ⚠️ Clear FIRST. The dock switches to the thread view the moment this is
      // called, so leaving the previous transcript standing while the fetch is
      // in flight — or after it fails — shows the merchant one conversation
      // while they believe they are reading another. Same rule as the thread
      // list two functions up: a read that did not happen is not a result.
      //
      // The bump is what makes every OTHER awaited write drop out: a reply to a
      // turn sent in the thread being replaced no longer matches.
      const seq = mintEpoch()
      claim(asked, (s) => ({
        ...s,
        messages: [],
        conversationId: undefined,
        threadSeq: seq,
        sending: false,
        opening: true,
        openFailed: false,
        live: undefined,
      }))

      let res
      try {
        res = await chatApi.getConversation(asked, conversationId)
      } catch {
        res = null
      }
      if (!stillCurrent(asked)) return
      // ⚠️ Click B then C: if C answers first, B's slower answer must not
      // replace the transcript AND the conversation id under it, leaving the
      // merchant reading B while believing C is open. The `seq` this call
      // minted is what `patchIfCurrent` below checks — B's is stale by then.

      if (!res?.success || !res.data) {
        patchIfCurrent(asked, seq, (s) => ({ ...s, opening: false, openFailed: true }))
        return
      }
      const detail = res.data
      patchIfCurrent(asked, seq, (s) => ({
        ...s,
        opening: false,
        openFailed: false,
        conversationId: detail.conversationId,
        messages: detail.messages.map((m) => ({
          id: m.id,
          content: m.content,
          role: m.role,
          timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
          sources: m.sources,
          priorSources: m.priorSources,
          // ⚠️ Legacy only. A turn written before MAR-173 PR 3 has fence-parsed
          // `toolOutputs` and no `sources`; new rows never carry it. Mapped here
          // and NOT in `send`, so a fresh reply can only ever render typed cards.
          toolOutputs: m.toolOutputs,
          // Stored without a token, so it can only ever render inert. Re-stripped
          // anyway: the transcript must not carry one whatever the wire sends.
          ...(m.proposal ? { proposal: withoutToken(m.proposal) } : {}),
        })),
      }))
    },
    [claim, liveWorkspaceId, mintEpoch, patchIfCurrent, stillCurrent]
  )

  const newThread = useCallback(() => {
    const asked = liveWorkspaceId
    if (!asked) return
    // Bumps the epoch too: a reply to the turn just abandoned must not land in
    // the empty thread the merchant just asked for and silently continue it.
    const seq = mintEpoch()
    claim(asked, (s) => ({
      ...s,
      messages: [],
      conversationId: undefined,
      threadSeq: seq,
      sending: false,
      opening: false,
      openFailed: false,
      live: undefined,
    }))
  }, [claim, liveWorkspaceId, mintEpoch])

  /**
   * ⚠️ `context` is an ARGUMENT, and that is the whole of §3.2's per-turn rule.
   *
   * "Changing pages changes the next turn's context; it does not rewrite prior
   * turns" holds only if the hint is captured at the instant of the send —
   * exactly like `threadSeq` and `conversationId` a few lines below, and for
   * exactly the same reason. A hint read from a ref, a hook parameter or a
   * closure would be whatever the merchant was looking at when the reply
   * *landed*, so navigating during a slow turn would file the answer under the
   * wrong screen. It is not stored on the thread either: the record of what the
   * merchant was viewing is the server's turn row, not this transient store.
   */
  /**
   * Failed messages a retry has already claimed. A ref, so two presses in one
   * tick — before the claim below commits and `storeRef` shows the message gone
   * — still resend once. Never released: the retried message is removed, and
   * the turn it becomes carries a new id.
   */
  const retried = useRef(new Set<string>())
  /** Message ids are unique even inside one millisecond. */
  const msgSeq = useRef(0)

  const send = useCallback(
    async (raw: string, context?: AskContextHint, options?: SendOptions): Promise<SendOutcome> => {
      const content = raw.trim()
      const asked = liveWorkspaceId
      if (!content || !asked) return 'not_sent'

      // Read the committed store rather than closing over it, and read it as
      // "mine only if it is this workspace's" — a store belonging to the
      // workspace we just left must lend this turn neither its conversation id
      // nor its thread epoch.
      //
      // ⚠️ The `workspaceId === asked` half is **currently unfalsifiable, and
      // it is honest to say so**: mutating it to `snapshot` kills no test,
      // because `loadThreads`'s effect re-`claim`s the store for the new
      // workspace before any event handler can run, so the old values are gone
      // by the time `send` looks. What the property rests on today is `claim`.
      // It earns its keep the day that load becomes conditional, lazy or
      // removed — the change that would otherwise reintroduce B's workspaceId
      // against A's conversationId with nothing to catch it.
      const snapshot = storeRef.current
      const mine = snapshot.workspaceId === asked ? snapshot : null
      // ⚠️ A turn we cannot scope is not sent. `mine` is null only if the store
      // does not yet belong to this workspace, which cannot happen today — the
      // thread-list effect claims it before any event handler can run — and if
      // it ever becomes reachable, FAILING CLOSED is the right answer: an
      // unscoped reply landing in whatever thread is open is precisely the bug
      // this epoch exists to prevent. The alternative, a fallback epoch, was
      // three lines no test could reach and one more `0` to collide with.
      if (!mine) return 'not_sent'
      if (mine.sending) return 'not_sent'
      const retryOf = options?.retryOf
      if (retryOf !== undefined) {
        const failed = mine.messages.find((m) => m.id === retryOf)
        if (!failed || failed.role !== 'user' || !failed.failure?.retryable) return 'not_sent'
        if (retried.current.has(retryOf)) return 'not_sent'
        retried.current.add(retryOf)
      }
      // The thread this turn belongs to, captured BEFORE the await. Without it
      // the reply lands in whichever thread happens to be open when it arrives.
      const seq = mine.threadSeq
      // ⚠️ Server-minted. The client never generates a conversation id; an
      // invented one forks a thread the server has no row for.
      const continuing = mine.conversationId

      const userId = `u-${Date.now()}-${++msgSeq.current}`
      claim(asked, (s) => ({
        ...s,
        sending: true,
        openFailed: false,
        messages: [
          // A retry replaces the failed turn rather than repeating it beside
          // its notice: the question is asked once more, not twice.
          ...(retryOf !== undefined ? s.messages.filter((m) => m.id !== retryOf) : s.messages),
          { id: userId, content, role: 'user' as const, timestamp: new Date() },
        ],
      }))

      let res
      try {
        res = await chatApi.sendMessage({
          message: content,
          workspaceId: asked,
          accountId,
          conversationId: continuing,
          // Omitted rather than sent as `undefined`: the server's schema is
          // strict and a hint is optional, so "no hint" is an absent key.
          ...(context ? { context } : {}),
        })
      } catch {
        res = null
      }

      if (!stillCurrent(asked)) return 'not_sent'

      if (res?.success && res.data) {
        const data = res.data
        const proposal = isLiveProposal(data.proposal) ? data.proposal : null
        patchIfCurrent(asked, seq, (s) => ({
          ...s,
          sending: false,
          conversationId: data.conversationId ?? s.conversationId,
          // The token goes HERE and the transcript gets the copy without it.
          live:
            proposal && data.conversationId
              ? {
                  conversationId: data.conversationId,
                  receiptId: proposal.receiptId,
                  token: proposal.token,
                  expiresAt: proposal.expiresAt,
                  working: false,
                }
              : s.live,
          messages: [
            ...s.messages,
            {
              id: `a-${Date.now()}-${++msgSeq.current}`,
              content: data.message,
              role: 'assistant' as const,
              timestamp: new Date(),
              sources: data.sources,
              priorSources: data.priorSources,
              ...(proposal ? { proposal: withoutToken(proposal) } : {}),
            },
          ],
        }))
        // A new or renamed thread has to reach the history view.
        if (data.conversationId) loadThreads(asked)
        return 'answered'
      }

      // The failure is NAMED on the merchant's own message (D-38): the turn is
      // marked failed and the dock draws a notice under it. It is NEVER appended
      // as a Marva message — that read as an answer, could not be retried, and
      // looked like a reply nobody sent.
      //
      // ⚠️ **A refusal the merchant can fix must not read as an outage.** Two
      // things produce a 400 here. A message over the server's
      // `MAX_MESSAGE_CHARS` answers `code:'message_too_long'`, and it gets its
      // own copy and no Try again: retrying can never fix it, trimming can. The
      // composer caps input at the same limit (`askLimits.ts`), so this is the
      // backstop, and the outcome tells the dock to put the text back.
      //
      // ⚠️ Everything ELSE still reads as UNREACHABLE, including a 400 for a
      // rejected `context`. That remains a DRIFT HAZARD: today `toContextHint`
      // cannot build an invalid hint, but the day a surface publishes a `view`
      // or the shell `AskEntity` gains a field somebody spreads onto the wire,
      // every turn from that surface fails looking like an outage — and offers a
      // Try again that fails the same way. The fix then is a server `code` for
      // it and a branch beside this one — match on `res.code`, never on the
      // prose in `res.error`.
      const tooLong = res?.code === MESSAGE_TOO_LONG
      const failure: AskFailure = tooLong
        ? { kind: 'too_long', copy: tooLongCopy(res?.maxChars), retryable: false }
        : { kind: 'unreachable', copy: UNREACHABLE, retryable: true }
      patchIfCurrent(asked, seq, (s) => ({
        ...s,
        sending: false,
        messages: s.messages.map((m) => (m.id === userId ? { ...m, failure } : m)),
      }))
      return tooLong ? 'too_long' : 'failed'
    },
    [accountId, claim, liveWorkspaceId, loadThreads, patchIfCurrent, stillCurrent]
  )

  /**
   * Presses in flight, by workspace + receipt. A ref, so two clicks in one tick still see the first.
   *
   * ⚠️ Released only once the OUTCOME HAS COMMITTED (`afterCommit`), never in a
   * `finally` around the request. Between the response and the commit,
   * `storeRef` still holds the unspent token, so a press landing in that gap
   * would pass every check above and send the token a second time.
   */
  const inFlight = useRef(new Set<string>())

  const actOnProposal = useCallback(
    async (receiptId: string, action: ProposalAction) => {
      const asked = liveWorkspaceId
      if (!asked) return
      const snapshot = storeRef.current
      // The same scoping as the read below, applied to the committed store: this
      // workspace, this thread, this proposal, and a token not yet spent.
      if (snapshot.workspaceId !== asked) return
      const live = snapshot.live
      if (!live || live.receiptId !== receiptId || !live.token) return
      if (snapshot.conversationId === undefined || live.conversationId !== snapshot.conversationId) return
      // Expired here is not asked about: the server would only say 410 — UNLESS an
      // earlier press is pending retry. That press may already have applied (the
      // server replays the receipt) or be mid-apply (it resumes), and only the
      // server can tell; a truly expired, never-applied proposal still answers 410.
      if (live.outcome?.kind !== 'retry' && proposalExpired(live.expiresAt, Date.now())) return
      const key = `${asked}|${receiptId}`
      if (inFlight.current.has(key)) return
      inFlight.current.add(key)

      const seq = snapshot.threadSeq
      const token = live.token
      const patchLive = (patch: (l: LiveProposal) => LiveProposal) =>
        patchIfCurrent(asked, seq, (s) =>
          s.live && s.live.receiptId === receiptId ? { ...s, live: patch(s.live) } : s
        )

      patchLive((l) => ({ ...l, working: true }))
      let res
      try {
        res =
          action === 'confirm'
            ? await governanceApi.confirmProposal(asked, token)
            : await governanceApi.cancelProposal(asked, token)
      } catch {
        res = null
      }
      const release = () => inFlight.current.delete(key)
      if (!stillCurrent(asked)) {
        release()
        return
      }
      const outcome = proposalOutcome(action, res)
      // Spent on every answer but "we could not tell": a retry is safe only
      // because a replayed confirm answers the original receipt.
      patchLive((l) => ({
        ...l,
        working: false,
        outcome,
        token: outcome.kind === 'retry' ? l.token : undefined,
      }))
      // If the patch was dropped (thread moved on) nothing re-renders for it, and
      // the key is released on the next commit of anything — by which point the
      // proposal it guards is no longer live.
      afterCommit.current.push(release)
    },
    [liveWorkspaceId, patchIfCurrent, stillCurrent]
  )

  // ── Scoped at the read. Another workspace's conversation is simply not here. ──
  const view = stored.workspaceId === liveWorkspaceId ? stored : EMPTY
  // ⚠️ And the proposal is scoped one dimension further: to the thread it was
  // prepared in. Named here, at the read, so no frame can show it elsewhere.
  const live =
    view.live && view.conversationId !== undefined && view.live.conversationId === view.conversationId
      ? view.live
      : undefined
  const liveProposal = useMemo<LiveProposalView | null>(
    () =>
      live
        ? {
            receiptId: live.receiptId,
            working: live.working,
            outcome: live.outcome,
            actionable: !!live.token && !live.working,
          }
        : null,
    [live]
  )

  return useMemo(
    () => ({
      threads: view.threads,
      threadsState: view.threadsState,
      messages: view.messages,
      conversationId: view.conversationId,
      sending: view.sending,
      opening: view.opening,
      openFailed: view.openFailed,
      openThread,
      newThread,
      send,
      liveProposal,
      actOnProposal,
    }),
    [view, openThread, newThread, send, liveProposal, actOnProposal]
  )
}
